/**
 * Finance · Revenue 360 Workspace (Wave 3).
 *
 * Question this workspace answers:
 *   "Where is my profit really coming from?"
 *
 * Contract-driven per Objects 019 (Revenue Stream) · 009 (Product) ·
 * 011 (SKU) · 010 (Order) · 014 (Batch) · 007 (Customer) · 020 (Campaign).
 *
 * Reuses existing primitives only — no new visual language.
 */
import { useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import { Card, SectionHeader, DeltaChip, Button } from "../../components/lib";
import { Drawer, Field, Segmented, ListRow } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import { RelatedLinksBlock } from "../../lib/integration";
import {
  REVENUE_STREAMS, PRODUCT_REVENUE, INVOICES,
  RevenueStream, ProductRevenue,
} from "../../mocks/financeData";
import { PRODUCTS, ORDERS } from "../../mocks/commerceData";
import { BATCHES } from "../../mocks/operationsData";
import { CAMPAIGNS } from "../../mocks/marketingData";
import { CUSTOMERS_360 } from "../../mocks/customers360Data";
import { linksForProductSku } from "../../mocks/entityLinks";

type StreamTab =
  | "overview" | "customers" | "invoices" | "orders" | "products"
  | "batches" | "campaigns" | "channels" | "reps" | "regions"
  | "returns" | "profit" | "audit";

type SkuTab =
  | "overview" | "customers" | "orders" | "batches" | "campaigns"
  | "channels" | "returns" | "profit" | "audit";

/** Derive channel string from RevenueStream.category so we can match Orders. */
function streamChannelKeys(c: RevenueStream["category"]): string[] {
  if (c === "web")       return ["web"];
  if (c === "wholesale") return ["wholesale", "b2b"];
  if (c === "pop-up")    return ["pop-up"];
  if (c === "affiliate") return ["affiliate"];
  return ["b2b", "wholesale"];
}

export default function Revenue() {
  const [stream, setStream] = useState<RevenueStream | null>(null);
  const [skuRow, setSkuRow] = useState<ProductRevenue | null>(null);
  const [sTab, setSTab] = useState<StreamTab>("overview");
  const [pTab, setPTab] = useState<SkuTab>("overview");

  const totalMonth = REVENUE_STREAMS.reduce((n, s) => n + s.monthEGP, 0);
  const totalYtd   = REVENUE_STREAMS.reduce((n, s) => n + s.ytdEGP, 0);
  const maxStream  = Math.max(1, ...REVENUE_STREAMS.map((s) => s.monthEGP));
  const maxProduct = Math.max(1, ...PRODUCT_REVENUE.map((p) => p.revenueEGP));

  // Blended margin & profit (portfolio-level) — surfaces the "profit" question up-front.
  const totalRevenue     = PRODUCT_REVENUE.reduce((n, p) => n + p.revenueEGP, 0);
  const blendedMarginPct = totalRevenue > 0
    ? PRODUCT_REVENUE.reduce((n, p) => n + p.revenueEGP * p.grossMarginPct, 0) / totalRevenue
    : 0;
  const blendedProfit    = totalRevenue * blendedMarginPct;
  const topSkuSharePct   = totalRevenue > 0 ? (PRODUCT_REVENUE[0]?.revenueEGP ?? 0) / totalRevenue : 0;

  // ── Stream drill-down (memo) ─────────────────────────────────────
  const streamData = useMemo(() => {
    if (!stream) return null;
    const channels = streamChannelKeys(stream.category);
    const orders = ORDERS.filter((o) => channels.includes(o.channel));
    const invoices = stream.category === "wholesale" || stream.category === "b2b"
      ? INVOICES.filter((i) => i.amountEGP > 0)
      : [];
    // Distinct customers by orders
    const custMap = new Map<string, { name: string; totalEGP: number; count: number; city: string }>();
    orders.forEach((o) => {
      const cur = custMap.get(o.customer) || { name: o.customer, totalEGP: 0, count: 0, city: o.city };
      cur.totalEGP += o.totalEGP;
      cur.count += 1;
      custMap.set(o.customer, cur);
    });
    const customers = Array.from(custMap.values()).sort((a, b) => b.totalEGP - a.totalEGP);
    // Products sold via these orders
    const skuMap = new Map<string, { sku: string; name: string; units: number; revenueEGP: number }>();
    orders.forEach((o) => o.lines.forEach((l) => {
      const cur = skuMap.get(l.sku) || { sku: l.sku, name: l.name, units: 0, revenueEGP: 0 };
      cur.units += l.qty;
      cur.revenueEGP += l.qty * l.unitEGP;
      skuMap.set(l.sku, cur);
    }));
    const skus = Array.from(skuMap.values()).sort((a, b) => b.revenueEGP - a.revenueEGP);
    // Batches supporting these SKUs
    const batches = BATCHES.filter((b) => skus.some((s) => s.sku === b.sku));
    // Campaigns attributed
    const campaigns = CAMPAIGNS.filter((c) => skus.some((s) => c.sku && (s.sku.includes(c.sku) || c.sku.includes(s.sku))));
    // Returns/refunds (best-effort proxy: refunded/cancelled orders)
    const returns = orders.filter((o) => o.status === "refunded" || o.status === "cancelled");
    // Blended margin on stream — use PRODUCT_REVENUE margin for matching SKUs, else 55% fallback
    const margins = skus.map((s) => {
      const pr = PRODUCT_REVENUE.find((p) => p.sku === s.sku);
      return { ...s, gm: pr ? pr.grossMarginPct : 0.55 };
    });
    const streamRevenue = margins.reduce((n, m) => n + m.revenueEGP, 0);
    const streamProfit  = margins.reduce((n, m) => n + m.revenueEGP * m.gm, 0);
    const streamMargin  = streamRevenue > 0 ? streamProfit / streamRevenue : 0;
    return { orders, invoices, customers, skus, batches, campaigns, returns, streamRevenue, streamProfit, streamMargin };
  }, [stream]);

  // ── SKU drill-down (memo) ────────────────────────────────────────
  const skuData = useMemo(() => {
    if (!skuRow) return null;
    const product = PRODUCTS.find((p) => p.sku === skuRow.sku);
    const orderLines: { order: typeof ORDERS[number]; qty: number; unitEGP: number }[] = [];
    ORDERS.forEach((o) => o.lines.forEach((l) => {
      if (l.sku === skuRow.sku) orderLines.push({ order: o, qty: l.qty, unitEGP: l.unitEGP });
    }));
    const custMap = new Map<string, number>();
    orderLines.forEach((l) => custMap.set(l.order.customer, (custMap.get(l.order.customer) || 0) + l.qty * l.unitEGP));
    const customers = Array.from(custMap.entries()).map(([name, totalEGP]) => ({ name, totalEGP })).sort((a, b) => b.totalEGP - a.totalEGP);
    const batches = BATCHES.filter((b) => b.sku === skuRow.sku);
    const campaigns = CAMPAIGNS.filter((c) => c.sku && (c.sku === skuRow.sku || skuRow.sku.includes(c.sku)));
    const channelMap = new Map<string, number>();
    orderLines.forEach((l) => channelMap.set(l.order.channel, (channelMap.get(l.order.channel) || 0) + l.qty * l.unitEGP));
    const channels = Array.from(channelMap.entries()).map(([ch, egp]) => ({ ch, egp })).sort((a, b) => b.egp - a.egp);
    const returns = orderLines.filter((l) => l.order.status === "refunded" || l.order.status === "cancelled");
    const profit = skuRow.revenueEGP * skuRow.grossMarginPct;
    const cogs = skuRow.revenueEGP - profit;
    return { product, orderLines, customers, batches, campaigns, channels, returns, profit, cogs };
  }, [skuRow]);

  return (
    <div className="space-y-6" data-testid="fn-revenue">
      {/* ── Portfolio KPIs ────────────────────────────────────────── */}
      <Card testId="fn-rev-summary">
        <SectionHeader eyebrow="30-day" title="Where is my profit really coming from?" action={<TrendingUp size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Month revenue</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>EGP {(totalMonth / 1000000).toFixed(2)}M</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>YTD</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>EGP {(totalYtd / 1000000).toFixed(1)}M</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Blended margin</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: blendedMarginPct >= 0.5 ? "var(--ph-success)" : "var(--ph-warning)" }}>{Math.round(blendedMarginPct * 100)}%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Est. profit (30d)</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>EGP {(blendedProfit / 1000000).toFixed(2)}M</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Top-SKU share</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-brand-primary)" }}>{Math.round(topSkuSharePct * 100)}%</div>
          </div>
        </div>
      </Card>

      {/* ── Streams ──────────────────────────────────────────────── */}
      <Card padded={false} testId="fn-rev-streams">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Streams · click to drill" title="By channel & geography" />
        </div>
        <div className="p-5 space-y-2">
          {REVENUE_STREAMS.map((s) => {
            const bar = Math.max(2, (s.monthEGP / maxStream) * 100);
            return (
              <button
                key={s.key}
                data-testid={`fn-rev-${s.key}`}
                onClick={() => { setStream(s); setSTab("overview"); }}
                className="grid items-center py-2 w-full text-left transition-colors"
                style={{ gridTemplateColumns: "2fr 3fr 1fr 1fr 0.7fr", borderTop: "1px solid var(--ph-border-subtle)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ph-hover-bg)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{s.label}</div>
                  <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>{s.category}</div>
                </div>
                <div>
                  <div className="h-2 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                    <div style={{ width: `${bar}%`, height: "100%", background: "var(--ph-brand-primary)" }} />
                  </div>
                </div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-strong)" }}>EGP {(s.monthEGP / 1000).toFixed(0)}K</div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-default)" }}>EGP {(s.ytdEGP / 1000000).toFixed(2)}M</div>
                <div className="flex justify-end"><DeltaChip delta={s.delta} trend={s.trend} /></div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* ── Top SKUs ─────────────────────────────────────────────── */}
      <Card padded={false} testId="fn-rev-products">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Top SKUs · click to drill" title="Revenue by product" />
        </div>
        <div className="p-5 space-y-2">
          {PRODUCT_REVENUE.map((p) => {
            const bar = Math.max(2, (p.revenueEGP / maxProduct) * 100);
            const profit = p.revenueEGP * p.grossMarginPct;
            return (
              <button
                key={p.sku}
                data-testid={`fn-rev-sku-${p.sku}`}
                onClick={() => { setSkuRow(p); setPTab("overview"); }}
                className="grid items-center py-2 w-full text-left transition-colors"
                style={{ gridTemplateColumns: "2fr 3fr 1fr 1fr 1fr 1fr", borderTop: "1px solid var(--ph-border-subtle)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ph-hover-bg)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div>
                  <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{p.sku}</div>
                  <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{p.name}</div>
                </div>
                <div>
                  <div className="h-2 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                    <div style={{ width: `${bar}%`, height: "100%", background: "var(--ph-brand-primary)" }} />
                  </div>
                </div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-default)" }}>{p.unitsSold}</div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-strong)" }}>EGP {(p.revenueEGP / 1000).toFixed(0)}K</div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-success)" }}>{Math.round(p.grossMarginPct * 100)}% GM</div>
                <div className="text-sm tabular-nums text-right font-semibold" style={{ color: "var(--ph-success)" }}>EGP {(profit / 1000).toFixed(0)}K</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* ── Stream drawer ────────────────────────────────────────── */}
      <Drawer open={Boolean(stream)} onClose={() => setStream(null)} title="Revenue Stream 360" testId="fn-rev-stream-drawer" width={720}>
        {stream && streamData && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-brand-primary)" }}>{stream.category}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{stream.label}</div>

            <div className="grid grid-cols-4 gap-3 mb-4">
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Month</div><div className="text-sm font-semibold tabular-nums">EGP {(stream.monthEGP / 1000).toFixed(0)}K</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>YTD</div><div className="text-sm font-semibold tabular-nums">EGP {(stream.ytdEGP / 1000000).toFixed(2)}M</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Profit</div><div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-success)" }}>EGP {(streamData.streamProfit / 1000).toFixed(0)}K</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Margin</div><div className="text-sm font-semibold tabular-nums" style={{ color: streamData.streamMargin >= 0.5 ? "var(--ph-success)" : "var(--ph-warning)" }}>{Math.round(streamData.streamMargin * 100)}%</div></div>
            </div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<StreamTab>
                testId="fn-rev-stream-tabs"
                value={sTab}
                onChange={setSTab}
                options={[
                  { value: "overview",  label: "Overview"  },
                  { value: "customers", label: "Customers" },
                  { value: "invoices",  label: "Invoices"  },
                  { value: "orders",    label: "Orders"    },
                  { value: "products",  label: "Products"  },
                  { value: "batches",   label: "Batches"   },
                  { value: "campaigns", label: "Campaigns" },
                  { value: "channels",  label: "Channels"  },
                  { value: "reps",      label: "Reps"      },
                  { value: "regions",   label: "Regions"   },
                  { value: "returns",   label: "Returns"   },
                  { value: "profit",    label: "Profit"    },
                  { value: "audit",     label: "Audit"     },
                ]}
              />
            </div>

            {sTab === "overview" && (
              <div data-testid="fn-rev-stream-overview">
                <Field label="Category"       inline><span className="text-sm">{stream.category}</span></Field>
                <Field label="Month revenue"  inline><span className="text-sm tabular-nums">EGP {stream.monthEGP.toLocaleString()}</span></Field>
                <Field label="YTD revenue"    inline><span className="text-sm tabular-nums">EGP {stream.ytdEGP.toLocaleString()}</span></Field>
                <Field label="Trend"          inline><DeltaChip delta={stream.delta} trend={stream.trend} /></Field>
                <Field label="Orders"         inline><span className="text-sm tabular-nums">{streamData.orders.length}</span></Field>
                <Field label="Distinct customers" inline><span className="text-sm tabular-nums">{streamData.customers.length}</span></Field>
                <Field label="SKUs sold"      inline><span className="text-sm tabular-nums">{streamData.skus.length}</span></Field>
                <Field label="Refunds/returns" inline><span className="text-sm tabular-nums" style={{ color: streamData.returns.length > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>{streamData.returns.length}</span></Field>
              </div>
            )}

            {sTab === "customers" && (
              <div data-testid="fn-rev-stream-customers">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Top customers · {streamData.customers.length}</div>
                {streamData.customers.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No customers linked.</div>}
                {streamData.customers.slice(0, 15).map((c) => {
                  const c360 = CUSTOMERS_360.find((x) => x.name === c.name);
                  return (
                    <ListRow
                      key={c.name}
                      testId={`fn-rev-cust-${encodeURIComponent(c.name)}`}
                      label={<>{c.name} <span className="text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>· {c.city}</span></>}
                      sublabel={<>{c.count} order{c.count === 1 ? "" : "s"}{c360 ? ` · ${c360.tier} · NPS ${c360.nps}` : ""}</>}
                      trailing={<span className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-ink-strong)" }}>EGP {c.totalEGP.toLocaleString()}</span>}
                    />
                  );
                })}
              </div>
            )}

            {sTab === "invoices" && (
              <div data-testid="fn-rev-stream-invoices">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Linked invoices · {streamData.invoices.length}</div>
                {streamData.invoices.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Retail streams don&rsquo;t produce invoices. See Orders instead.</div>}
                {streamData.invoices.slice(0, 20).map((i) => (
                  <ListRow
                    key={i.id}
                    testId={`fn-rev-inv-${i.id}`}
                    label={<>{i.customer} <span className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{i.number}</span></>}
                    sublabel={<>{i.status} · {i.dueRelative}</>}
                    trailing={<span className="text-sm tabular-nums font-semibold">EGP {i.amountEGP.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {sTab === "orders" && (
              <div data-testid="fn-rev-stream-orders">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Orders · {streamData.orders.length}</div>
                {streamData.orders.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No orders in this stream.</div>}
                {streamData.orders.slice(0, 25).map((o) => (
                  <ListRow
                    key={o.id}
                    testId={`fn-rev-ord-${o.id}`}
                    label={<>{o.reference} <span className="text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>· {o.customer}</span></>}
                    sublabel={<>{o.status} · {o.placedRelative} · {o.city}</>}
                    trailing={<span className="text-sm tabular-nums font-semibold">EGP {o.totalEGP.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {sTab === "products" && (
              <div data-testid="fn-rev-stream-products">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>SKUs in this stream · {streamData.skus.length}</div>
                {streamData.skus.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No SKUs sold.</div>}
                {streamData.skus.slice(0, 20).map((s) => {
                  const pr = PRODUCT_REVENUE.find((p) => p.sku === s.sku);
                  return (
                    <ListRow
                      key={s.sku}
                      testId={`fn-rev-str-sku-${s.sku}`}
                      label={<>{s.name} <span className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{s.sku}</span></>}
                      sublabel={<>{s.units} units{pr ? ` · GM ${Math.round(pr.grossMarginPct * 100)}%` : ""}</>}
                      trailing={<span className="text-sm tabular-nums font-semibold">EGP {s.revenueEGP.toLocaleString()}</span>}
                    />
                  );
                })}
              </div>
            )}

            {sTab === "batches" && (
              <div data-testid="fn-rev-stream-batches">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Batches feeding this stream · {streamData.batches.length}</div>
                {streamData.batches.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No batches identified.</div>}
                {streamData.batches.slice(0, 15).map((b) => (
                  <ListRow
                    key={b.id}
                    testId={`fn-rev-batch-${b.id}`}
                    label={<>{b.code} <span className="text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>· {b.productName}</span></>}
                    sublabel={<>{b.stage} · {b.units} units · {b.startedRelative}</>}
                  />
                ))}
              </div>
            )}

            {sTab === "campaigns" && (
              <div data-testid="fn-rev-stream-campaigns">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Attributed campaigns · {streamData.campaigns.length}</div>
                {streamData.campaigns.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No campaigns attributed.</div>}
                {streamData.campaigns.map((c) => (
                  <ListRow
                    key={c.id}
                    testId={`fn-rev-cmp-${c.id}`}
                    label={<>{c.name} <span className="text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>· {c.channels.join(", ")}</span></>}
                    sublabel={<>{c.status} · budget EGP {(c.budgetEGP || 0).toLocaleString()} · ROAS {c.roas.toFixed(2)}×</>}
                    trailing={<span className="text-sm tabular-nums">EGP {(c.spendEGP || 0).toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {sTab === "channels" && (
              <div data-testid="fn-rev-stream-channels">
                <Field label="Primary channels" inline><span className="text-sm">{streamChannelKeys(stream.category).join(", ")}</span></Field>
                <Field label="Total orders"     inline><span className="text-sm tabular-nums">{streamData.orders.length}</span></Field>
                <Field label="Ø order value"    inline><span className="text-sm tabular-nums">EGP {streamData.orders.length ? Math.round(streamData.orders.reduce((n, o) => n + o.totalEGP, 0) / streamData.orders.length).toLocaleString() : 0}</span></Field>
              </div>
            )}

            {sTab === "reps" && (
              <div data-testid="fn-rev-stream-reps">
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Sales-rep attribution isn&rsquo;t captured on retail streams. For wholesale, rep is tracked at the invoice level — see Invoice 360.</div>
              </div>
            )}

            {sTab === "regions" && (
              <div data-testid="fn-rev-stream-regions">
                {(() => {
                  const map = new Map<string, number>();
                  streamData.orders.forEach((o) => map.set(o.city, (map.get(o.city) || 0) + o.totalEGP));
                  const rows = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
                  const maxV = Math.max(1, ...rows.map((r) => r[1]));
                  return rows.length === 0
                    ? <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No region data.</div>
                    : rows.map(([city, egp]) => (
                        <div key={city} className="grid items-center py-1.5" style={{ gridTemplateColumns: "1.4fr 3fr 1fr" }}>
                          <div className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>{city}</div>
                          <div className="h-2 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                            <div style={{ width: `${(egp / maxV) * 100}%`, height: "100%", background: "var(--ph-brand-primary)" }} />
                          </div>
                          <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-strong)" }}>EGP {(egp / 1000).toFixed(0)}K</div>
                        </div>
                      ));
                })()}
              </div>
            )}

            {sTab === "returns" && (
              <div data-testid="fn-rev-stream-returns">
                <Field label="Refunded/cancelled orders" inline><span className="text-sm tabular-nums" style={{ color: streamData.returns.length > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>{streamData.returns.length}</span></Field>
                <Field label="Return rate" inline><span className="text-sm tabular-nums">{streamData.orders.length ? ((streamData.returns.length / streamData.orders.length) * 100).toFixed(1) : "0.0"}%</span></Field>
                <Field label="Refunded value" inline><span className="text-sm tabular-nums" style={{ color: "var(--ph-danger)" }}>EGP {streamData.returns.reduce((n, o) => n + o.totalEGP, 0).toLocaleString()}</span></Field>
                <div className="mt-2">
                  {streamData.returns.map((o) => (
                    <ListRow
                      key={o.id}
                      testId={`fn-rev-ret-${o.id}`}
                      label={<>{o.reference} · {o.customer}</>}
                      sublabel={<>{o.status} · {o.placedRelative}</>}
                      trailing={<span className="text-sm tabular-nums" style={{ color: "var(--ph-danger)" }}>EGP {o.totalEGP.toLocaleString()}</span>}
                    />
                  ))}
                </div>
              </div>
            )}

            {sTab === "profit" && (
              <div data-testid="fn-rev-stream-profit">
                <Field label="Revenue"      inline><span className="text-sm tabular-nums font-semibold">EGP {streamData.streamRevenue.toLocaleString()}</span></Field>
                <Field label="Est. COGS"    inline><span className="text-sm tabular-nums" style={{ color: "var(--ph-danger)" }}>EGP {(streamData.streamRevenue - streamData.streamProfit).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                <Field label="Gross profit" inline><span className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-success)" }}>EGP {streamData.streamProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                <Field label="Margin"       inline><span className="text-sm tabular-nums font-semibold" style={{ color: streamData.streamMargin >= 0.5 ? "var(--ph-success)" : "var(--ph-warning)" }}>{Math.round(streamData.streamMargin * 100)}%</span></Field>
              </div>
            )}

            {sTab === "audit" && (
              <div data-testid="fn-rev-stream-audit">
                <AuditTrail collection="revenue_streams" recordId={stream.key} testId="fn-rev-stream-audit-list" />
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* ── SKU drawer ───────────────────────────────────────────── */}
      <Drawer open={Boolean(skuRow)} onClose={() => setSkuRow(null)} title="SKU Revenue 360" testId="fn-rev-sku-drawer" width={720}>
        {skuRow && skuData && (
          <div>
            <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{skuRow.sku}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{skuRow.name}</div>

            <div className="grid grid-cols-4 gap-3 mb-4">
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Revenue</div><div className="text-sm font-semibold tabular-nums">EGP {(skuRow.revenueEGP / 1000).toFixed(0)}K</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Units</div><div className="text-sm font-semibold tabular-nums">{skuRow.unitsSold}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Profit</div><div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-success)" }}>EGP {(skuData.profit / 1000).toFixed(0)}K</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Margin</div><div className="text-sm font-semibold tabular-nums" style={{ color: skuRow.grossMarginPct >= 0.5 ? "var(--ph-success)" : "var(--ph-warning)" }}>{Math.round(skuRow.grossMarginPct * 100)}%</div></div>
            </div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<SkuTab>
                testId="fn-rev-sku-tabs"
                value={pTab}
                onChange={setPTab}
                options={[
                  { value: "overview",  label: "Overview"  },
                  { value: "customers", label: "Customers" },
                  { value: "orders",    label: "Orders"    },
                  { value: "batches",   label: "Batches"   },
                  { value: "campaigns", label: "Campaigns" },
                  { value: "channels",  label: "Channels"  },
                  { value: "returns",   label: "Returns"   },
                  { value: "profit",    label: "Profit"    },
                  { value: "audit",     label: "Audit"     },
                ]}
              />
            </div>

            {pTab === "overview" && (
              <div data-testid="fn-rev-sku-overview">
                <Field label="Product name"   inline><span className="text-sm">{skuRow.name}</span></Field>
                <Field label="SKU"            inline><span className="text-sm font-mono">{skuRow.sku}</span></Field>
                <Field label="Family"         inline><span className="text-sm">{skuData.product?.family || "—"}</span></Field>
                <Field label="Units sold"     inline><span className="text-sm tabular-nums">{skuRow.unitsSold}</span></Field>
                <Field label="Revenue"        inline><span className="text-sm tabular-nums">EGP {skuRow.revenueEGP.toLocaleString()}</span></Field>
                <Field label="Gross margin"   inline><span className="text-sm tabular-nums" style={{ color: "var(--ph-success)" }}>{Math.round(skuRow.grossMarginPct * 100)}%</span></Field>
              </div>
            )}

            {pTab === "customers" && (
              <div data-testid="fn-rev-sku-customers">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Customers who bought this SKU · {skuData.customers.length}</div>
                {skuData.customers.slice(0, 15).map((c) => (
                  <ListRow key={c.name} testId={`fn-rev-sku-cust-${encodeURIComponent(c.name)}`}
                    label={c.name}
                    trailing={<span className="text-sm tabular-nums">EGP {c.totalEGP.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {pTab === "orders" && (
              <div data-testid="fn-rev-sku-orders">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Order lines · {skuData.orderLines.length}</div>
                {skuData.orderLines.slice(0, 25).map((l, idx) => (
                  <ListRow key={`${l.order.id}-${idx}`}
                    testId={`fn-rev-sku-line-${l.order.id}`}
                    label={<>{l.order.reference} · {l.order.customer}</>}
                    sublabel={<>{l.qty} × EGP {l.unitEGP.toLocaleString()} · {l.order.status}</>}
                    trailing={<span className="text-sm tabular-nums">EGP {(l.qty * l.unitEGP).toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {pTab === "batches" && (
              <div data-testid="fn-rev-sku-batches">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Batches supplying this SKU · {skuData.batches.length}</div>
                {skuData.batches.map((b) => (
                  <ListRow key={b.id} testId={`fn-rev-sku-batch-${b.id}`}
                    label={b.code}
                    sublabel={<>{b.stage} · {b.units} units</>}
                  />
                ))}
              </div>
            )}

            {pTab === "campaigns" && (
              <div data-testid="fn-rev-sku-campaigns">
                {skuData.campaigns.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No campaigns linked.</div>}
                {skuData.campaigns.map((c) => (
                  <ListRow key={c.id} testId={`fn-rev-sku-cmp-${c.id}`}
                    label={c.name}
                    sublabel={<>{c.status} · ROAS {c.roas.toFixed(2)}×</>}
                    trailing={<span className="text-sm tabular-nums">EGP {(c.spendEGP || 0).toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {pTab === "channels" && (
              <div data-testid="fn-rev-sku-channels">
                {skuData.channels.map((c) => (
                  <div key={c.ch} className="grid items-center py-1.5" style={{ gridTemplateColumns: "1.4fr 3fr 1fr" }}>
                    <div className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>{c.ch}</div>
                    <div className="h-2 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                      <div style={{ width: `${Math.max(2, (c.egp / (skuData.channels[0]?.egp || 1)) * 100)}%`, height: "100%", background: "var(--ph-brand-primary)" }} />
                    </div>
                    <div className="text-sm tabular-nums text-right">EGP {(c.egp / 1000).toFixed(0)}K</div>
                  </div>
                ))}
              </div>
            )}

            {pTab === "returns" && (
              <div data-testid="fn-rev-sku-returns">
                <Field label="Refunded lines" inline><span className="text-sm tabular-nums" style={{ color: skuData.returns.length > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>{skuData.returns.length}</span></Field>
                <Field label="Refunded value" inline><span className="text-sm tabular-nums" style={{ color: "var(--ph-danger)" }}>EGP {skuData.returns.reduce((n, l) => n + l.qty * l.unitEGP, 0).toLocaleString()}</span></Field>
              </div>
            )}

            {pTab === "profit" && (
              <div data-testid="fn-rev-sku-profit">
                <Field label="Revenue"      inline><span className="text-sm tabular-nums font-semibold">EGP {skuRow.revenueEGP.toLocaleString()}</span></Field>
                <Field label="COGS"         inline><span className="text-sm tabular-nums" style={{ color: "var(--ph-danger)" }}>EGP {skuData.cogs.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                <Field label="Gross profit" inline><span className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-success)" }}>EGP {skuData.profit.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                <Field label="Ø unit price" inline><span className="text-sm tabular-nums">EGP {skuRow.unitsSold ? Math.round(skuRow.revenueEGP / skuRow.unitsSold).toLocaleString() : 0}</span></Field>
              </div>
            )}

            {pTab === "audit" && (
              <div data-testid="fn-rev-sku-audit">
                <AuditTrail collection="product_revenue" recordId={skuRow.sku} testId="fn-rev-sku-audit-list" />
              </div>
            )}

            <RelatedLinksBlock links={linksForProductSku(skuRow.sku)} testId="fn-rev-sku-related" />
            <div className="pt-4 flex justify-end">
              <Button variant="secondary" testId="fn-rev-sku-close" onClick={() => setSkuRow(null)}>Close</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
